import type { FileInfo } from "@exagens/behavioral-insight-console-client";
import type { SearchIndexRow } from "@/features/sparxsEditor/lib/advancedSearch/types/searchTypes";
import type { SparxsBlueprintCacheEntry } from "@/store/sparxsCatalog/blueprintCacheSlice";

export const blueprintRawContentSlotCount = (
  blueprint: { content?: unknown } | undefined,
): number => {
  if (blueprint == null) {
    return 0;
  }
  const raw = blueprint.content;
  if (Array.isArray(raw)) {
    return raw.length;
  }
  if (raw !== null && typeof raw === "object") {
    return Object.keys(raw).length;
  }
  return 0;
};

/** Stable string for fingerprint segments (primitives only; objects/arrays via JSON). */
const fingerprintValue = (value: unknown): string => {
  if (value == null) {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (typeof value === "bigint") {
    return value.toString();
  }
  return JSON.stringify(value);
};

/** Stable fingerprint of every own key on list `FileInfo` (sorted). Do not hardcode fields. */
export const fileInfoFingerprint = (f: FileInfo): string => {
  const record = f as unknown as Record<string, unknown>;
  const keys = Object.keys(record).sort((a, b) => a.localeCompare(b));
  return keys.map((k) => `${k}=${fingerprintValue(record[k])}`).join("\t");
};

export const catalogFingerprint = (
  files: FileInfo[],
  excludedFiles: FileInfo[],
): string => {
  const sortedFiles = [...files].sort((a, b) =>
    a.fileName.localeCompare(b.fileName),
  );
  const sortedExcluded = [...excludedFiles].sort((a, b) =>
    a.fileName.localeCompare(b.fileName),
  );
  return [
    "files",
    ...sortedFiles.map(fileInfoFingerprint),
    "excluded",
    ...sortedExcluded.map(fileInfoFingerprint),
  ].join("\n");
};

export const isCatalogFileNewerThanStoredListVersion = (
  file: FileInfo,
  storedListLastModified: number | undefined,
): boolean => {
  const listLm = file.lastModified;
  if (listLm === undefined) {
    return false;
  }
  if (storedListLastModified === undefined) {
    return true;
  }
  return storedListLastModified !== listLm;
};

export const needsSearchIndexRefetch = (
  file: FileInfo,
  row: SearchIndexRow | undefined,
  cache?: SparxsBlueprintCacheEntry,
): boolean => {
  if (row == null) {
    return true;
  }
  if (cache == null) {
    return true;
  }
  const liveFp = fileInfoFingerprint(file);
  if (row.listFingerprint == null || row.listFingerprint === "") {
    return true;
  }
  if (row.listFingerprint !== liveFp) {
    return true;
  }
  const slotCount = blueprintRawContentSlotCount(cache.blueprint);
  if (slotCount !== row.contentCount) {
    return true;
  }
  return false;
};
